
import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { HubConnection, HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { showGlobalToast } from '../utils/toast';
import { EventPayload } from '../models/signalREvent';
import { playRingtone } from '../utils/ringtonePlayer';

interface SignalRContextType {
    connection: HubConnection | null;
    // subscribeToEvent: (eventKey: string, pageKey: string, callback: () => void) => void;
    subscribeToEvent: (eventKey: string, pageKey: string, callback: (data?: any) => void) => void;
    unsubscribeFromEvent: (eventKey: string, pageKey: string) => void;
}

const SignalRContext = createContext<SignalRContextType | null>(null);

export const useSignalR = () => {
    const context = useContext(SignalRContext);
    if (!context) {
        throw new Error('useSignalR must be used within a SignalRProvider');
    }
    return context;
};

interface SignalRProviderProps {
    hubUrl: string;
    groupName?: string;
    userRole: string; // e.g., 'customer', 'manager';
    restaurantGuid?: string;
    tableId?: string;
    children: ReactNode;
}

type EventCallback = {
    pageKey: string;
    // callback: () => void;
    callback: (data?: any) => void;
};

export const SignalRProvider: React.FC<SignalRProviderProps> = ({ hubUrl, groupName, userRole, restaurantGuid, tableId, children }) => {
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const eventCallbacks = useRef<Map<string, EventCallback[]>>(new Map());

    const subscribeToEvent = useCallback((eventKey: string, pageKey: string, callback: () => void) => {
        const callbacks = eventCallbacks.current.get(eventKey) || [];
        if (!callbacks.some((item: EventCallback) => item.pageKey === pageKey)) {
            eventCallbacks.current.set(eventKey, [...callbacks, { pageKey, callback }]);
        }
    }, []);

    const unsubscribeFromEvent = useCallback((eventKey: string, pageKey: string) => {
        const callbacks = eventCallbacks.current.get(eventKey) || [];
        const filtered = callbacks.filter((item: EventCallback) => item.pageKey !== pageKey);
        if (filtered.length > 0) {
            eventCallbacks.current.set(eventKey, filtered);
        } else {
            eventCallbacks.current.delete(eventKey);
        }
    }, []);

    useEffect(() => {
        const newConnection = new HubConnectionBuilder()
            .withUrl(hubUrl)
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        setConnection(newConnection);

        return () => {
            newConnection.stop();
        };
    }, [hubUrl]);

    useEffect(() => {
        if (connection && connection.state === 'Disconnected') {
            connection.start()
                .then(() => {
                    console.log('SignalR Connected!');
                    if (userRole === 'customer') {
                        connection.invoke('JoinGroup', restaurantGuid, tableId).catch(err => console.error(`Failed to join group ${groupName}`, err));
                    }
                    else {
                        connection.invoke('JoinGroup', restaurantGuid).catch(err => console.error(`Failed to join group ${groupName}`, err));
                    }

                    connection.on('ReceiveAppEvent', (event: EventPayload) => {
                        const isRoleAllowed = !event.roles || event.roles.length === 0 || event.roles.includes(userRole);
                        if (!isRoleAllowed) {
                            return;
                        }

                        if (event.isShowGlobal && event.message) {
                            showGlobalToast(event.message, 'success');
                        }

                        if (event.soundCategory && event.soundCategory !== '') {
                            console.log('Playing sound for category:', event.soundCategory);
                            const isMuted = localStorage.getItem('IsSoundMuted') === 'true';
                            if (!isMuted) {
                                playRingtone('new_order_01');  // later will changes based on event.playSound which is catergory
                            }
                        }

                        if (event.eventType === 'PageEvent' && event.eventKey) {
                            const callbacks = eventCallbacks.current.get(event.eventKey);
                            if (!callbacks) return;

                            callbacks.forEach((cb: EventCallback) => {
                                if (event.isShowGlobal && event.message) {
                                    showGlobalToast(event.message, 'success');
                                }
                                // if (event.isApiCallRequired) {
                                //     cb.callback();
                                // }
                                if (event.isApiCallRequired || event.data) {
                                    cb.callback(event.data ?? null);
                                }
                            });
                        }
                    });
                })
                .catch(err => console.error('SignalR Connection Error: ', err));
        }

        return () => {
            connection?.off('ReceiveAppEvent');
            if (connection) {
                if (userRole === 'customer' && restaurantGuid && tableId) {
                    connection.invoke('LeaveGroup', restaurantGuid, tableId)
                        .then(() => console.log(`Left group for table ${tableId}`))
                        .catch(err => console.error(`Failed to leave table group`, err));
                } else if (restaurantGuid) {
                    connection.invoke('LeaveGroup', restaurantGuid)
                        .then(() => console.log(`Left group for restaurant ${restaurantGuid}`))
                        .catch(err => console.error(`Failed to leave restaurant group`, err));
                }
            }
        };
    }, [connection, groupName, eventCallbacks, userRole, restaurantGuid, tableId]);

    return (
        <SignalRContext.Provider value={{ connection, subscribeToEvent, unsubscribeFromEvent }}>
            {children}
        </SignalRContext.Provider>
    );
};
